import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { MAX_ATTACHMENTS } from 'Pods/ThreeDStudio/constants'
import useImageAssetUpload from 'Pods/ThreeDStudio/Hooks/useImageAssetUpload'
import useRequireGenerationAccess from 'Pods/ThreeDStudio/Hooks/useRequireGenerationAccess'
import useSessionImageGenerations from 'Pods/ThreeDStudio/Hooks/useSessionImageGenerations'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { buildImageGeneratePayload } from 'Pods/ThreeDStudio/Services/buildImageGeneratePayload'
import { imageSchemaFor } from 'Pods/ThreeDStudio/Shared/Utils/imageModels'
import { subscribeToGeneratorRun, VERSION_KIND } from 'Pods/ThreeDStudio/Shared/Utils/imageRun'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { IMAGE_ACTION } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY, NOOP, EMPTY_OBJECT } from 'Utils/stableDefaults'

import { buildVersionChain } from './buildVersionChain'
import useMaskCanvas from './useMaskCanvas'

/**
 * Owns the Edit canvas state: the image loaded onto the mask canvas, the composer
 * (prompt + reference attachments), the generated-version history, and the History
 * dropdown. Composes useMaskCanvas for the drawing tools. A painted mask runs a
 * masked edit (base image + red-overlay marker); an instruction alone runs a
 * single-image prompt edit.
 *
 * Everything the surface owns is handed in rather than read from the store, so the
 * assistant panel's Edit tab and the full-width editor can each render the hook
 * against their own image and session without sharing state.
 *
 * @param {object} args
 * @param {{ id: number|string, url: string, alt?: string }} args.image - image to edit
 * @param {number|string|undefined} args.sessionId - generator session the lineage is read from
 * @param {boolean} args.isActive - whether this surface is the one on screen; entering it
 *   drops reference attachments, which a masked edit would never send
 * @param {() => number|string|undefined} args.getSessionId - the session id read at send
 *   time, so a session created moments ago by the first run is picked up
 * @param {(sessionId: number|string) => void} args.onSessionCreated - called with the
 *   session the backend put a freshly created run in
 * @param {() => void} args.onStartNew - leaves the editor to compose a new image
 */
export default function useEditCanvas({
  image,
  sessionId,
  isActive = true,
  getSessionId = null,
  onSessionCreated = NOOP,
  onStartNew = NOOP,
}) {
  const t = useTranslation()
  const { generations } = useSessionImageGenerations(sessionId)
  const canvas = useMaskCanvas()

  const [currentImage, setCurrentImage] = useState(image)
  const [versions, setVersions] = useState(EMPTY_ARRAY)
  // The image nothing was edited from. The handed-in image until a lineage is restored,
  // which may reveal that it is itself an edit of something earlier.
  const [original, setOriginal] = useState(image)
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  // The loaded image is the Original, so the first edit of it is Version 1.
  const versionCountRef = useRef(0)
  const isSendingRef = useRef(false)
  const isMountedRef = useRef(true)
  const subscriptionsRef = useRef(new Set())
  const restoredRef = useRef(false)

  useEffect(() => () => {
    isMountedRef.current = false
    subscriptionsRef.current.forEach(subscription => subscription.unsubscribe())
    subscriptionsRef.current.clear()
  }, [])

  // Run creation reads the session id at send time, not at render time, so a session the
  // backend created for the first run is picked up by the second. Surfaces that hold the
  // id outside React pass a getter; the rest fall back to the rendered value.
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId
  const readSessionId = useCallback(
    () => (getSessionId ? getSessionId() : sessionIdRef.current),
    [getSessionId],
  )

  const queryClient = useQueryClient()
  const { postData } = useApiPost()
  const uploadImageAsset = useImageAssetUpload()
  const { requireGenerationAccess, isAccessPending } = useRequireGenerationAccess()

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      if (prev.length === 0) {
        return prev
      }
      prev.forEach(item => URL.revokeObjectURL(item.url))

      return EMPTY_ARRAY
    })
  }, [])

  // A masked edit sends only the base image and the overlay, so the upload control is
  // hidden here; entering the surface also drops anything already attached rather than
  // leaving chips that would never be sent.
  useEffect(() => {
    if (isActive) {
      clearAttachments()
    }
  }, [isActive, clearAttachments])

  // The whole lineage this image belongs to is put back in the History dropdown, so
  // applying a middle version and reopening still shows every version around it rather
  // than just what came after. currentImage is deliberately left as the image that was
  // handed in: the surface is only ever entered by picking an image, and loading a
  // different one would open something the user did not click. The Toolbar highlights it
  // by id, and selecting another version switches to it.
  useEffect(() => {
    if (restoredRef.current || !image) {
      return
    }

    const lineage = buildVersionChain(generations, image.id)
    if (lineage.length === 0) {
      return
    }
    restoredRef.current = true
    // Keeps new labels from colliding with the restored ones.
    versionCountRef.current = lineage.length - 1
    // The lineage starts with the image nothing produced, which is the original; the
    // edits after it are Version 1 onwards, held newest first as applyVersion prepends.
    const [originalAsset, ...edits] = lineage
    setOriginal(originalAsset)
    setVersions(edits
      .map((version, index) => ({
        ...version,
        label: t('imageAssistantPanel.editTab.toolbar.version', { number: index + 1 }),
      }))
      .reverse())
    // The handed-in image stays loaded, but the lineage knows how it was made — carry
    // that over so the prompt viewer can show the instruction that produced it.
    const loadedEntry = lineage.find(version => String(version.id) === String(image.id))
    if (loadedEntry?.prompt) {
      setCurrentImage(prev => ({ ...prev, kind: loadedEntry.kind, prompt: loadedEntry.prompt }))
    }
  }, [generations, image, t])

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(attachment => attachment.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(attachment => attachment.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file), file }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  const stopSending = useCallback(() => {
    if (!isMountedRef.current) {
      return
    }
    isSendingRef.current = false
    setIsSending(false)
  }, [])

  const failEdit = useCallback(() => {
    stopSending()
    if (typeof window !== 'undefined' && window.showNotification) {
      window.showNotification(t('imageAssistantPanel.editTab.editFailed'), { type: 'error', autohide: true })
    }
  }, [stopSending, t])

  // The image is already a Generator::Asset, so reuse its id as the 3D input rather
  // than re-uploading the file. The panel deliberately stays open: this now runs as a
  // side effect of finishing an edit or picking a version, not as an explicit "apply
  // and continue" the user asked to leave on.
  const applyToInput = useCallback((version) => {
    if (!version) {
      return
    }
    setInputImage(version.id, version.url)
  }, [])

  const applyVersion = useCallback((asset, meta) => {
    if (!isMountedRef.current) {
      return
    }
    versionCountRef.current += 1
    // A live edit owns the history from here on, so a late-arriving restore is skipped.
    restoredRef.current = true
    const version = {
      id: asset.assetId,
      url: asset.fileUrl,
      label: t('imageAssistantPanel.editTab.toolbar.version', { number: versionCountRef.current }),
      kind: meta?.kind,
      prompt: meta?.prompt,
      runId: meta?.runId,
    }
    // Newest first so the History dropdown lists the latest version on top.
    setVersions(prev => [version, ...prev])
    setCurrentImage(version)
    applyToInput(version)
    setPrompt('')
    clearAttachments()
    canvas.clear()
    stopSending()
  }, [canvas, clearAttachments, stopSending, applyToInput, t])

  // Wait for the run's websocket terminal update, then add the edited image as a new
  // version (or surface a failure). meta records how the version was made (kind) and
  // with which instruction (prompt) for the history subtitle and the prompt viewer.
  const subscribeToRun = useCallback((runId, meta) => {
    const subscription = subscribeToGeneratorRun(runId, {
      onImage: (asset) => {
        subscriptionsRef.current.delete(subscription)
        applyVersion(asset, { ...meta, runId })
        queryClient.refetchQueries({ queryKey: ['libraryItems', 'images'], exact: false })
      },
      onFail: () => {
        subscriptionsRef.current.delete(subscription)
        failEdit()
      },
    })

    if (subscription) {
      subscriptionsRef.current.add(subscription)
    } else {
      failEdit()
    }
  }, [applyVersion, failEdit, queryClient])

  // Send needs an edit instruction and a loaded image. With a painted mask it uploads
  // the red-overlay marker and starts a multi-image-edit run with overlay_mask: true:
  // input_image is the base image (Image 1) and the overlay (Image 2), ordered by asset
  // position. Without one it starts an image-edit run on the base image alone, applying
  // the instruction verbatim.
  //
  // The run is built from the action's schema without a model_id or the composer settings,
  // so the backend ranks the providers offering it instead of the run being locked to one,
  // and keeps its default aspect ratio and single image. Both edit actions cap
  // input_image (multi-image-edit with overlay_mask exactly 2, image-edit exactly 1) in
  // both catalogs, so reference attachments are not part of the payload - an extra image
  // is rejected.
  const handleSend = useCallback(() => {
    const editText = prompt.trim()
    if (isSendingRef.current || !editText || !currentImage || !requireGenerationAccess()) {
      return
    }
    isSendingRef.current = true
    setIsSending(true)

    const baseAssetId = currentImage.id
    const meta = {
      kind: canvas.hasPainted ? VERSION_KIND.BRUSH : VERSION_KIND.PROMPT,
      prompt: editText,
    }

    const startRun = (action, inputImage, runSessionId, extraParams = EMPTY_OBJECT) => {
      postData({
        path: '/api/internal/generator/runs',
        data: buildImageGeneratePayload(
          imageSchemaFor(action),
          { prompt: editText, input_image: inputImage, ...extraParams },
          null,
          runSessionId ? { generator_session_id: runSessionId } : EMPTY_OBJECT,
        ),
        onSuccess: (response) => {
          const { id, generatorSessionId } = response.data.data.attributes
          if (generatorSessionId) {
            onSessionCreated(generatorSessionId)
          }
          subscribeToRun(id, meta)
        },
        onError: () => failEdit(),
      })
    }

    if (!canvas.hasPainted) {
      startRun(IMAGE_ACTION.IMAGE_EDIT, [{ asset_id: baseAssetId }], readSessionId())
      return
    }

    canvas.toOverlayFile(currentImage.url)
      .then((overlayFile) => {
        if (!overlayFile) {
          throw new Error('Failed to build mask overlay')
        }
        // Read at send time rather than reusing the rendered sessionId, so a session
        // created moments ago by the first run is picked up.
        const runSessionId = readSessionId()

        return uploadImageAsset(overlayFile, { sessionId: runSessionId })
          .then(maskAssetId => ({ maskAssetId, runSessionId }))
      })
      .then(({ maskAssetId, runSessionId }) => {
        const overlayImages = [{ asset_id: baseAssetId }, { asset_id: maskAssetId }]
        startRun(IMAGE_ACTION.MULTI_IMAGE_EDIT, overlayImages, runSessionId, { overlay_mask: true })
      })
      .catch(() => failEdit())
  }, [
    canvas,
    prompt,
    currentImage,
    postData,
    readSessionId,
    onSessionCreated,
    uploadImageAsset,
    subscribeToRun,
    failEdit,
    requireGenerationAccess,
  ])

  const handleToggleHistory = useCallback(() => setIsHistoryOpen(prev => !prev), [])

  const handleCloseHistory = useCallback(() => setIsHistoryOpen(false), [])

  const handleSelectVersion = useCallback((version) => {
    setCurrentImage(version)
    applyToInput(version)
    setIsHistoryOpen(false)
    canvas.clear()
  }, [canvas, applyToInput])

  // Keep the latest attachments in a ref so the unmount cleanup revokes the
  // current object URLs without re-running (and revoking still-rendered URLs) on
  // every attachment change. Removal and send already revoke their own URLs.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  useEffect(() => () => {
    attachmentsRef.current.forEach(item => URL.revokeObjectURL(item.url))
  }, [])

  // The original closes out the history, so the user can always get back to it. Edits
  // are held newest first, so it goes last.
  const historyVersions = useMemo(() => {
    if (!original) {
      return versions
    }

    return [...versions, {
      id: original.id,
      url: original.url,
      label: t('imageAssistantPanel.editTab.toolbar.originalVersion'),
    }]
  }, [versions, original, t])

  return {
    canvas,
    currentImage,
    versions: historyVersions,
    versionCount: versions.length,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    // A click while the tier is still hydrating cannot be judged, so the guard would
    // swallow it; the button waits instead.
    isSendDisabled: isSending || isAccessPending || !prompt.trim(),
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleStartNew: onStartNew,
  }
}
